import React from 'react';
import clsx from 'clsx';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import {
  notEmptyString,
} from '@populateui/utils';
import Empty from 'core/components/Empty';
import StatusTag from 'core/components/StatusTag';
import {
  TempoCommuniqueActivityDTO,
  TempoScoreDTO,
} from 'dtos';
import {
  TempoSeverity,
} from 'dtos/tempoSeverity';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  formatDateTime,
} from 'utils/date';
import ToolTip from 'core/components/Tooltip';

type Props = {
  title: string;
  status?: string | null;
  activities: TempoCommuniqueActivityDTO[];
};

type ActivityIconProps = {
  activityType?: string | null;
};

const activityStatusMap = {
  pending: 'Pending',
  inprogress: 'Sent',
  reminder: 'Reminder',
  success: 'Completed',
  completed: 'Completed',
};

function ActivityIcon({ activityType }: ActivityIconProps) {
  return activityType === 'Completed'
    ? <CheckRoundedIcon className="!h-4 !w-4" />
    : <SendRoundedIcon className="!h-4 !w-4" />;
}

const INSTRUMENT_TOOLTIPS: Record<string, string> = {
  PHQ9: 'Screens for depression by scoring how often depressive symptoms occurred (0–27).',
  GAD7: 'Screens for anxiety by scoring how often worry, nervousness, and related symptoms occurred (0–21).',
  SSS8: 'Measures how much common physical symptoms, such as pain, fatigue, and sleep trouble, bothered the patient (0–32).',
  SOMATICSYMPTOMSCALE8SSS8: 'Measures how much common physical symptoms, such as pain, fatigue, and sleep trouble, bothered the patient (0–32).',
  SOMATICSYMPTOMSCALE8: 'Measures how much common physical symptoms, such as pain, fatigue, and sleep trouble, bothered the patient (0–32).',
  ASRS: 'Screens for adult ADHD by rating how often inattention, hyperactivity, and impulsivity occur, with severity based on the count of high-frequency responses.',
  MDQ: 'Screens for bipolar-spectrum disorders by assessing past manic or hypomanic symptoms, whether they occurred together, and how much they impaired functioning.',
  CSSRS: 'Stratifies suicide risk by weighting passive ideation, method, intent or plan, and suicidal behavior.',
  COLUMBIASUICIDESEVERITYRATINGSCALE: 'Stratifies suicide risk by weighting passive ideation, method, intent or plan, and suicidal behavior.',
  NIDAMODIFIEDASSIST: 'A substance use screen that measures how often the patient used alcohol and drugs in the past year and what consequences followed, including loss of control, hazardous use, and failed attempts to cut back.',
  NIDAASSIST: 'A substance use screen that measures how often the patient used alcohol and drugs in the past year and what consequences followed, including loss of control, hazardous use, and failed attempts to cut back.',
  SSD: 'A structured interview that assesses the distress, health-related worry, time burden, and duration tied to the patient\'s physical symptoms, to evaluate for somatic symptom disorder.',
  SSDCLINICALINTERVIEW: 'A structured interview that assesses the distress, health-related worry, time burden, and duration tied to the patient\'s physical symptoms, to evaluate for somatic symptom disorder.',
};

const SEVERITY_CONFIG_MAP: Record<string, { container: string; text: string }> = {
  red: { container: 'border-red-200 bg-red-50/70', text: 'text-red-700' },
  severe: { container: 'border-red-200 bg-red-50/70', text: 'text-red-700' },
  yellow: { container: 'border-amber-200 bg-amber-50/70', text: 'text-amber-800' },
  watch: { container: 'border-amber-200 bg-amber-50/70', text: 'text-amber-800' },
  amber: { container: 'border-amber-200 bg-amber-50/70', text: 'text-amber-800' },
  green: { container: 'border-emerald-200 bg-emerald-50/50', text: 'text-emerald-700' },
  ok: { container: 'border-emerald-200 bg-emerald-50/50', text: 'text-emerald-700' },
  blue: { container: 'border-emerald-200 bg-emerald-50/50', text: 'text-emerald-700' },
};

const DEFAULT_SEVERITY_CONFIG = {
  container: 'border-blue-100 bg-blue-50/50',
  text: 'text-blue-700',
};

function getInstrumentTooltip(instrument?: string | null): string | undefined {
  const normalizedKey = instrument?.toUpperCase().replace(/[^A-Z0-9]/g, '') ?? '';
  return INSTRUMENT_TOOLTIPS[normalizedKey];
}

function getSeverityConfig(severity?: TempoSeverity | string | null) {
  const key = severity?.toLowerCase() ?? '';
  return SEVERITY_CONFIG_MAP[key] ?? DEFAULT_SEVERITY_CONFIG;
}

type ActivityScoresProps = {
  scores?: TempoScoreDTO[] | null;
};

function ActivityScores({ scores }: ActivityScoresProps) {
  return (scores?.length ?? 0) > 0 ? (
    <div className="mt-3">
      <h5 className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Survey scores
        <span className="ml-1 font-normal normal-case tracking-normal">(raw)</span>
      </h5>
      <dl className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {scores?.map((score) => {
          const tooltipText = getInstrumentTooltip(score.instrument);
          const severityConfig = getSeverityConfig(score.severity);
          const instrumentLabel = notEmptyString(score.instrument) ? score.instrument : 'Assessment';

          return (
            <div
              key={`${score.instrument ?? 'score'}-${score.rawScore ?? 'missing'}`}
              className={clsx(
                'flex min-h-[40px] min-w-0 items-center justify-between gap-2 rounded-md border px-2.5 py-2',
                severityConfig.container,
              )}
            >
              <dt className="flex min-w-0 items-center gap-1 break-words text-[11px] font-medium leading-4 text-gray-700">
                <span className="min-w-0 truncate sm:whitespace-normal">
                  {instrumentLabel}
                </span>
                {notEmptyString(tooltipText) && (
                  <ToolTip
                    title={(
                      <div className="max-w-[260px] p-0.5 text-left text-xs font-normal leading-relaxed text-gray-700 whitespace-normal break-words">
                        {tooltipText}
                      </div>
                    )}
                    placement="top"
                  >
                    <span className="inline-flex cursor-help items-center text-gray-400 hover:text-gray-600">
                      <HelpOutlineRoundedIcon className="!h-3.5 !w-3.5" />
                    </span>
                  </ToolTip>
                )}
              </dt>
              <dd className="flex shrink-0 items-center">
                <span className={clsx(
                  'text-base font-semibold leading-5 tabular-nums',
                  severityConfig.text,
                  )}>
                  {score.rawScore ?? DEFAULT_EMPTY_VALUE}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  ) : null;
}

export default function TempoActivityTimeline({ title, status, activities }: Props) {
  const latestActivity = activities[activities.length - 1];
  const latestStatus = status ?? latestActivity?.status ?? latestActivity?.activityType;
  const isHeaderCompleted = latestStatus?.toLowerCase() === 'completed';

  return (
    <section className="flex min-h-[300px] min-w-0 flex-col rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-gray-800">
          {title}
        </h4>
        {notEmptyString(latestStatus) && (
          <StatusTag
            value={latestStatus ?? ""}
            statusMap={activityStatusMap}
            className={clsx(
              '!h-6 !px-2 !inline-flex !items-center',
              { '!bg-green-200': isHeaderCompleted },
            )}
            classes={{
              root: clsx('!inline-flex !items-center', { '!bg-green-200': isHeaderCompleted }),
              value: clsx('!gap-1 !items-center !text-[10px] !leading-4', { '!text-green-700': isHeaderCompleted }),
            }}
          />
        )}
      </div>
      {activities.length === 0 ? (
        <Empty>
          <span className="text-xs text-gray-500">No activity yet</span>
        </Empty>
      ) : (
        <Timeline
          className="!m-0 !mt-4 !p-0"
          sx={{
            '& .MuiTimelineItem-root:before': { display: 'none' },
          }}
        >
          {activities.map((activity, index) => {
            const isCompleted = activity.activityType === 'Completed';
            const isActivityCompleted = activity.status?.toLowerCase() === 'completed';
            const hasRecipientDetails = notEmptyString(activity?.description);

            return (
              <TimelineItem key={activity.activityId ?? `${activity.activityType}-${index}`}>
                <TimelineSeparator>
                  <TimelineDot
                    className={clsx(
                      '!m-0 !h-6 !w-6 !items-center !justify-center !p-0',
                      {
                        '!border-green-700 !bg-green-700 !text-white': isCompleted,
                        '!border-blue-200 !bg-blue-50 !text-blue-600': !isCompleted,
                      },
                    )}
                  >
                    <ActivityIcon activityType={activity.activityType} />
                  </TimelineDot>
                  {index < activities.length - 1 && (
                    <TimelineConnector className="!w-px !bg-gray-200" />
                  )}
                </TimelineSeparator>
                <TimelineContent className="!-mt-1 min-w-0 !pb-5 !pl-3 !pt-0">
                  <div className="text-[10px] text-gray-500">
                    {activity.occurredOn ? formatDateTime(activity.occurredOn) : ''}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-gray-800">
                    {activity.title}
                  </div>
                  {(activity.status || activity.channel) && (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {activity.status && (
                        <StatusTag
                          value={activity.status}
                          statusMap={activityStatusMap}
                          className={clsx(
                            '!h-5 !px-1.5 !inline-flex !items-center',
                            { '!bg-green-200': isActivityCompleted },
                          )}
                          classes={{
                            root: clsx('!inline-flex !items-center', { '!bg-green-200': isActivityCompleted }),
                            value: clsx('!gap-1 !items-center !text-[9px] !leading-4', { '!text-green-700': isActivityCompleted }),
                          }}
                        />
                      )}
                      {activity.channel && (
                        <ToolTip
                          hide={!hasRecipientDetails}
                          title={(
                            <div className="flex max-w-xs flex-col gap-1 p-1 text-left">
                              <span className="text-xs font-semibold text-gray-800">Sent to</span>
                              <span className="whitespace-pre-line break-words text-xs leading-5 text-gray-600">
                                {activity.description?.replace(/,\s*/g, '\n')}
                              </span>
                            </div>
                          )}
                        >
                          <span
                            className={clsx('text-[10px] text-gray-500', {
                              'cursor-help underline decoration-dotted underline-offset-2': hasRecipientDetails,
                            })}
                          >
                            {activity.channel}
                          </span>
                        </ToolTip>
                      )}
                    </div>
                  )}
                  <ActivityScores scores={activity.scores} />
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      )}
    </section>
  );
}
